import reportService, { localReportService } from '@/services/report/report-service';
import moment from 'moment';
import store from '@/store';

/**
 * Get report status list by barcode/accession ID
 * @param {string} accessionId - The barcode/accession ID
 * @returns {Promise} - API response with report status list
 */
export const getReportStatusList = async (accessionId) => {
  try {
    const response = await reportService.get(`result/getReportStatusListV2?barcode=${accessionId}`);
    return response.data;
  } catch (error) {
    console.error(`Failed to get report status for accession ID: ${accessionId}`, error);
    throw error;
  }
};

/**
 * Generate report URL
 * @param {string} barcode - Accession ID
 * @param {boolean} isStaging - Whether to use staging environment
 * @param {string} type - Report type (e.g., 'AllReport')
 * @param {boolean} isPrintable - Whether report is printable
 * @param {boolean} isEmail - Whether for email
 * @param {boolean} isBeta - Whether to use beta environment
 * @param {string} reportStyle - Report style ('classic' or 'advanced')
 * @returns {string} - Generated report URL
 */
const generateUrl = (barcode, isStaging, type, isPrintable, isEmail, isBeta = false, reportStyle = '') => {
  let url = '';
  const zone_name = Intl?.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();
  const token = store.state.login.token;
  
  if (isBeta) {
    url += 'https://main-beta.report-pdf.pages.dev/#/';
  } else {
    url += isStaging ? 'https://report.vibrant-wellness.com/#/' : 'https://report.vibrant-wellness.com/#/';
  }
  
  url += isPrintable ? 'printable/' : '';
  url += type + '/';
  url += barcode;
  url += '?startPageNumber=1';
  url += reportStyle ? '&&style=' + reportStyle : '';
  url += isStaging ? '&&staging=true' : '';
  url += '&&localTimeZone=' + zone_name;
  url += isEmail ? '' : '&&jwtToken=' + token;
  
  return url;
};

/**
 * View complete report (Classic style)
 * @param {string} accessionId - Accession ID
 */
export const viewCompleteReportClassic = async (accessionId) => {
  try {
    const isStaging = process.env.VUE_APP_ENV !== 'production';
    const url = generateUrl(accessionId, isStaging, 'AllReport', false, false, false, 'classic');
    window.open(url, '_blank')?.focus();
  } catch (error) {
    console.error('Failed to view classic report:', error);
    throw error;
  }
};

/**
 * View complete report (Modern/Advanced style)
 * @param {string} accessionId - Accession ID
 */
export const viewCompleteReportModern = async (accessionId) => {
  try {
    const isStaging = process.env.VUE_APP_ENV !== 'production';
    const url = generateUrl(accessionId, isStaging, 'AllReport', false, false, false, 'advanced');
    window.open(url, '_blank')?.focus();
  } catch (error) {
    console.error('Failed to view modern report:', error);
    throw error;
  }
};